import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { auth, firestore } from '../../firebase';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';

/**
 * FeatureAnnouncement
 *
 * A multi-page announcement modal for new features that persists user dismissal state in Firestore.
 *
 * Props:
 * - features: array of feature objects with { alertId, title, description, icon, illustration }
 */
const FeatureAnnouncement = ({ features = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [seenFeatures, setSeenFeatures] = useState(new Set());
  
  const isDarkMode = useMemo(
    () => document.documentElement.classList.contains('dark'),
    []
  );

  // Filter out features that have been seen
  const unseenFeatures = features.filter(feature => !seenFeatures.has(feature.alertId));
  const totalPages = unseenFeatures.length;
  const currentFeature = unseenFeatures[currentPage];

  const nextPage = () => {
    setCurrentPage((prev) => (prev + 1) % totalPages);
  };

  const prevPage = () => {
    setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);
  };

  useEffect(() => {
    const checkSeen = async () => {
      const user = auth.currentUser;
      if (!user || features.length === 0) return;
      try {
        const seenRef = doc(firestore, 'feature_alerts_by_user', user.uid);
        const snap = await getDoc(seenRef);
        const data = snap.exists() ? snap.data() : {};
        
        // Check which features have been seen
        const seenFeatureIds = new Set();
        features.forEach(feature => {
          if (data[feature.alertId]) {
            seenFeatureIds.add(feature.alertId);
          }
        });
        
        setSeenFeatures(seenFeatureIds);
        
        // Show modal if there are unseen features
        const hasUnseenFeatures = features.some(feature => !seenFeatureIds.has(feature.alertId));
        setIsOpen(hasUnseenFeatures);
      } catch (err) {
        // Fallback: still show the alert if we can't read state
        setIsOpen(true);
        // eslint-disable-next-line no-console
        console.error('Failed to check feature alert state:', err);
      }
    };
    checkSeen();
  }, [features]);

  const markAllFeaturesSeen = async () => {
    const user = auth.currentUser;
    if (!user || features.length === 0) return;
    try {
      const seenRef = doc(firestore, 'feature_alerts_by_user', user.uid);
      const updates = {};
      features.forEach(feature => {
        updates[feature.alertId] = true;
      });
      updates.updatedAt = serverTimestamp();
      
      await setDoc(seenRef, updates, { merge: true });
      
      // Update local state
      setSeenFeatures(new Set(features.map(f => f.alertId)));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to mark all features as seen:', err);
    }
  };

  // Auto-close modal when no more unseen features
  useEffect(() => {
    if (unseenFeatures.length === 0 && isOpen) {
      setIsOpen(false);
    }
  }, [unseenFeatures.length, isOpen]);

  // Reset currentPage when unseenFeatures changes
  useEffect(() => {
    if (currentPage >= unseenFeatures.length && unseenFeatures.length > 0) {
      setCurrentPage(0);
    }
  }, [unseenFeatures.length, currentPage]);

  const handleClose = async () => {
    setIsOpen(false);
    await markAllFeaturesSeen();
  };


  if (!isOpen || !currentFeature) return null;

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />

      <div
        className={`relative w-full max-w-xl mx-4 rounded-2xl shadow-2xl overflow-hidden transition-all ${
          isDarkMode ? 'bg-neutral-900' : 'bg-white'
        }`}
      >
        {/* Top illustration area */}
        <div className="relative h-40 overflow-hidden">
          {currentFeature.illustration}
        </div>

        {/* Content */}
        <button
          type="button"
          onClick={handleClose}
          className="absolute right-3 top-3 text-white/90 hover:text-white"
          aria-label="Close"
        >
          <X size={20} />
        </button>


        <div className="p-6">
          {/* Page indicator */}
          {totalPages > 1 && (
            <div className="flex justify-center mb-4 space-x-2">
              {unseenFeatures.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentPage
                      ? 'bg-indigo-500'
                      : isDarkMode
                      ? 'bg-gray-600'
                      : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          )}

          <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {currentFeature.title}
          </h3>
          <p className={`mt-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {currentFeature.description}
          </p>

          {/* Navigation buttons */}
          {totalPages > 1 && (
            <div className="mt-6 flex justify-center gap-4">
              <button
                type="button"
                onClick={prevPage}
                className={`p-3 rounded-full transition-all ${
                  isDarkMode
                    ? 'bg-neutral-800 text-white hover:bg-neutral-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                aria-label="Previous page"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                type="button"
                onClick={nextPage}
                className={`p-3 rounded-full transition-all ${
                  isDarkMode
                    ? 'bg-neutral-800 text-white hover:bg-neutral-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                aria-label="Next page"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default FeatureAnnouncement;

